'use client';

import { IcCloseCircle, IconButton } from '@musinsa/mds';
import { useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { tv } from 'tailwind-variants';
import type { Platform } from '../constants/platforms';

const inputVariants = tv({
  base: 'w-full border-none outline-none focus:border-none focus:outline-none',
  variants: {
    hasClearButton: {
      true: 'pr-8',
      false: '',
    },
  },
  defaultVariants: {
    hasClearButton: false,
  },
});

interface URLInputWithPrefixProps {
  platform?: Platform;
  value: string | undefined;
  onChange: (value: string) => void;
  onClear?: () => void;
  showClearButton?: boolean;
  className?: string;
}

export const URLInputWithPrefix = ({
  platform,
  value,
  onChange,
  onClear,
  showClearButton = false,
  className,
}: URLInputWithPrefixProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const hasPrefix = platform?.prefix && platform.prefix.length > 0;
  const placeholder = platform?.placeholder || 'URL';

  const safeValue = value ?? '';

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const onClearClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear?.();
  };

  return (
    <div className={twMerge('relative w-full', className)}>
      <div className="group inline-flex relative w-full [&>input]:pr-9">
        <div
          className={twMerge(
            'flex gap-0 py-[6px] px-2 w-full border rounded',
            'border-gray-300 group-focus-within:border-gray-500',
            'p-[7px]',
            !platform ? 'bg-gray-100' : '',
          )}
        >
          {hasPrefix && (
            <span className="text-gray-700 font-['Pretendard']" style={{ fontSize: '14px' }}>
              {platform.prefix}
            </span>
          )}
          <input
            ref={inputRef}
            className={inputVariants({
              hasClearButton: showClearButton && Boolean(safeValue),
            })}
            style={{
              fontSize: '14px',
              fontFamily: 'Pretendard',
            }}
            type="text"
            value={safeValue}
            onChange={onInputChange}
            placeholder={placeholder}
            disabled={!platform}
          />
          {showClearButton && safeValue && onClear && (
            <IconButton
              onClick={onClearClick}
              aria-label="텍스트 삭제"
              size="size20x20"
              className="absolute top-2 right-2"
            >
              <IcCloseCircle fillColor="gray-400" />
            </IconButton>
          )}
        </div>
      </div>
    </div>
  );
};
